import type { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';
import styled, { css } from 'styled-components';

import type { PressPageFields } from '@seuk/cms-api-client';
import { EntryContentTypeFieldsEnum, REVALIDATE_INTERVAL } from '@seuk/cms-api-client';
import type { NextImageProps } from '@seuk/design-system/atoms';
import { ZebraSection, Container, Main } from '@seuk/design-system/atoms';
import { Breadcrumbs } from '@seuk/design-system/molecules';
import { cmsAssetToImageProps } from '@seuk/util';

import { Hero } from '../../components';
import { HeroDetails } from '../../components/hero/hero-details';
import type { PressArticlesProps } from '../../components/press-articles/press-articles';
import { PressArticles } from '../../components/press-articles/press-articles';
import PageItem from '../../components/text-page-item';
import TextPageSectionItem from '../../components/text-page-item-section';
import { getCMSClient } from '../../services/cms';
import type { TextItem } from '../../util/section';
import { richTextSectionItemMapper } from '../../util/section';
import type { SEO } from '../../util/seo';
import { extractCMSSEO } from '../../util/seo';

interface PressPageProps {
  hero?: {
    image: NextImageProps;
    imageAlt: string;
    imageAlignment?: PressPageFields['heroImageAlignment'];
    title?: string;
    subtitle?: string;
  };
  sections?: readonly TextItem[];
  seo: SEO;
  shortArticles: PressArticlesProps['articles'];
}

const StyledZebraSection = styled(ZebraSection)`
  ${({ theme }) => css`
    padding: ${theme.spacing(8)} ${theme.spacing(12)};
  `}
`;

const BREADCRUMBS = [{ label: 'Home', href: '/' }, { label: 'Press' }];
export default function PressPage({ hero, sections, seo, shortArticles }: PressPageProps) {
  return (
    <>
      <NextSeo
        title={seo?.title}
        description={seo?.description}
        openGraph={{ images: [{ url: typeof hero.image.src === 'string' ? hero.image.src : undefined }] }}
        nofollow={seo?.nofollow}
        noindex={seo?.noindex}
      />

      <Main>
        <Container>
          <Breadcrumbs items={BREADCRUMBS} backHref="/" />
        </Container>
        <Hero heroImage={hero.image} bgAlign={hero.imageAlignment} heroAltText={hero.imageAlt} id="hero">
          {hero.title && <HeroDetails title={hero.title} subtitle={hero.subtitle} />}
        </Hero>

        <Container>
          {sections.map((section) => (
            <StyledZebraSection key={section.id}>
              <PageItem title={section.title}>
                <TextPageSectionItem item={section} trackInfo={{ pageTitle: hero.title }} />
              </PageItem>
            </StyledZebraSection>
          ))}
          <StyledZebraSection>
            <PressArticles articles={shortArticles} />
          </StyledZebraSection>
        </Container>
      </Main>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({ preview }) => {
  const [entry, articles] = await Promise.all([
    getCMSClient(preview).getEntryByFieldValue(EntryContentTypeFieldsEnum.PagePress, {}),
    getCMSClient(preview).getAllEntries({ contentType: EntryContentTypeFieldsEnum.PagePressArticle }),
  ]);

  const props: PressPageProps = {
    hero: {
      image: cmsAssetToImageProps(entry.fields.heroImage.fields.image),
      imageAlt: entry.fields.heroImage.fields.altText,
      imageAlignment: entry.fields.heroImageAlignment,
      title: entry.fields.heroTitle ?? null,
      subtitle: entry.fields.heroSubtitle ?? null,
    },
    shortArticles: Array.from(articles)
      .sort((a, b) => {
        return Number(new Date(b.fields.date)) - Number(new Date(a.fields.date));
      })
      .map((article) => {
        return {
          date: article.fields.date,
          title: article.fields.name,
          description: article.fields.description,
          slug: article.fields.slug,
        };
      }),
    seo: entry.fields.seo ? extractCMSSEO(entry.fields.seo.fields) : null,
    sections: await Promise.all(entry.fields.sections?.map(richTextSectionItemMapper) ?? []),
  };

  return {
    props,
    revalidate: REVALIDATE_INTERVAL,
  };
};
